import { useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import { UserAttributesConditionsManager } from 'components/admin/settings/userAttributes';
import {
  Checkbox,
  FormGroup,
  LanguageVariants,
  Text
} from 'components/common/form';
import { SiteSettingsForm } from '.';

import type { GetUserCustomAttributesRes } from 'shared-types/responses';

const SiteSettingsAchievements = () => {
  const { messages, serverConfig } = useReduxState();

  const {
    achievementsCertificateAllowAddingDefaultValue,
    individualAchievementsDisplayForAdmins,
    individualAchievementsEnabled,
    individualAchievementsUpcomingEnabled,
    individualAchievementsUpcomingOptions,
    individualAchievementsUserAllow,
    individualAchievementsUserRequireApproval,
    individualAchievementsUserRequireApprovalOptions
  } = serverConfig;

  const { data } = useApiData<GetUserCustomAttributesRes>({
    url: '/admin/user-custom-attributes'
  });

  const userCustomAttributes = data?.userCustomAttributes || [];

  const availableAttributes = {};
  userCustomAttributes.forEach((availableAttribute) => {
    availableAttributes[availableAttribute.key] = availableAttribute;
  });

  const initialValues = {
    achievementsCertificateAllowAddingDefaultValue,
    individualAchievementsDisplayForAdmins,
    individualAchievementsEnabled,
    individualAchievementsUpcomingEnabled,
    individualAchievementsUpcomingOptions,
    individualAchievementsUserAllow,
    individualAchievementsUserRequireApproval,
    individualAchievementsUserRequireApprovalOptions
  };

  return (
    <div className="settings-common">
      <SiteSettingsForm
        initialValues={initialValues}
        title={messages.commonAchievementsAlt}
      >
        {(formState) => (
          <div>
            <FormGroup label={messages.commonAchievements}>
              <Checkbox
                id="achievements-certificate-allow-adding-default-value"
                field="achievementsCertificateAllowAddingDefaultValue"
                label="Allow users to add attachment to their own completion by default"
              />
            </FormGroup>

            <FormGroup
              htmlFor="individual-achievements-display-for-admins"
              label={messages.individualAchievements}
            >
              <Checkbox
                field="individualAchievementsEnabled"
                id="individual-achievements-enabled"
                label="Enable individual achievements"
              />

              <Checkbox
                field="individualAchievementsDisplayForAdmins"
                id="individual-achievements-display-for-admins"
                label={messages.individualAchievementsDisplayForAdmin}
                style={{ marginTop: 5 }}
              />
            </FormGroup>

            <FormGroup label="Upcoming individual achievements">
              <Checkbox
                field="individualAchievementsUpcomingEnabled"
                id="individual-achievements-upcoming-enabled"
                label="Enable upcoming individual achievements"
              />
            </FormGroup>

            <FormGroup label="Upcoming individual achievements question">
              <Text
                disabled={
                  !formState.values.individualAchievementsUpcomingEnabled
                }
                field="individualAchievementsUpcomingOptions.question"
                placeholder={messages.achievementsIndividualCreateChoice}
              />

              <LanguageVariants
                field="individualAchievementsUpcomingOptions.questionTranslations"
                type="text"
              />
            </FormGroup>

            <FormGroup maxWidth="100%">
              <div className="row-md">
                <div className="col-1of2">
                  <FormGroup label="Upcoming individual achievements option completed">
                    <Text
                      disabled={
                        !formState.values.individualAchievementsUpcomingEnabled
                      }
                      field="individualAchievementsUpcomingOptions.optionCompleted"
                      placeholder={messages.achievementsIndividualCreatePast}
                      style={{ maxWidth: 300 }}
                    />

                    <LanguageVariants
                      field="individualAchievementsUpcomingOptions.optionCompletedTranslations"
                      style={{ maxWidth: 340 }}
                      type="text"
                    />
                  </FormGroup>
                </div>

                <div className="col-1of2">
                  <FormGroup label="Upcoming individual achievements option upcoming">
                    <Text
                      disabled={
                        !formState.values.individualAchievementsUpcomingEnabled
                      }
                      field="individualAchievementsUpcomingOptions.optionUpcoming"
                      placeholder={
                        messages.achievementsIndividualCreateUpcoming
                      }
                      style={{ maxWidth: 300 }}
                    />

                    <LanguageVariants
                      field="individualAchievementsUpcomingOptions.optionUpcomingTranslations"
                      style={{ maxWidth: 340 }}
                      type="text"
                    />
                  </FormGroup>
                </div>
              </div>
            </FormGroup>

            <FormGroup
              htmlFor="individual-achievements-for-users"
              label="Individual achievements for users"
              maxWidth="100%"
              style={{ marginTop: 25 }}
            >
              <Checkbox
                field="individualAchievementsUserAllow.enabled"
                id="individual-achievements-user-allow"
                label="Allow users to add individual achievements for themselves"
              />

              <UserAttributesConditionsManager
                availableAttributes={availableAttributes}
                fieldPrefix="individualAchievementsUserAllow"
                includeEmailCondition={true}
                includeEmailDomainCondition={true}
                label=""
                style={{ marginTop: 5 }}
              />
            </FormGroup>

            <FormGroup
              htmlFor="individual-achievements-admin-approval"
              label="Individual achievements admin approval"
            >
              <Checkbox
                field="individualAchievementsUserRequireApproval"
                id="individual-achievements-user-require-approval"
                label="Require admin approval"
              />
            </FormGroup>

            {formState.values.individualAchievementsUserRequireApproval && (
              <>
                <FormGroup label={'Supervisors'} maxWidth="100%">
                  <Checkbox
                    field="individualAchievementsUserRequireApprovalOptions.supervisor.enabled"
                    id="individual-achievements-user-require-approval-options-supervisor"
                    label="Allow supervisors to manage requests"
                  />

                  <UserAttributesConditionsManager
                    availableAttributes={availableAttributes}
                    fieldPrefix="individualAchievementsUserRequireApprovalOptions.supervisor"
                    includeEmailCondition={true}
                    includeEmailDomainCondition={true}
                    label=""
                    style={{ marginTop: 5 }}
                  />

                  <Checkbox
                    field="individualAchievementsUserRequireApprovalOptions.supervisor.sendEmailNotification"
                    id="individual-achievements-user-require-approval-options-supervisor-send-email-notification"
                    label="Send email notification about pending approvals to supervisors"
                    style={{ marginTop: 15 }}
                  />
                </FormGroup>

                <FormGroup label={'Superadmin notifications'} maxWidth="100%">
                  <Checkbox
                    field="individualAchievementsUserRequireApprovalOptions.superadminNotifications.sendEmailNotification"
                    id="individual-achievements-user-require-approval-options-admin-send-email-notification"
                    label="Send email notification about pending approvals to superadmins"
                  />

                  <UserAttributesConditionsManager
                    availableAttributes={availableAttributes}
                    fieldPrefix="individualAchievementsUserRequireApprovalOptions.superadminNotifications"
                    includeEmailCondition={true}
                    includeEmailDomainCondition={true}
                    label=""
                    style={{ marginTop: 5 }}
                  />

                  <Checkbox
                    field="individualAchievementsUserRequireApprovalOptions.superadminNotifications.sendEmailNotificationOnlyIfNoSupervisor"
                    id="individual-achievements-user-require-approval-options-admin-send-email-notification-only-if-no-supervisor"
                    label="Send notification only if user does not have a supervisor"
                    style={{ marginTop: 15 }}
                  />
                </FormGroup>
              </>
            )}
          </div>
        )}
      </SiteSettingsForm>
    </div>
  );
};

export default SiteSettingsAchievements;
